<template>
  <v-card class="mb-4" outlined>
    <v-card-text class="pa-4">
      <div class="d-flex align-start">
        <v-avatar size="36" color="primary" class="mr-3 mt-1">
          <v-icon dark>mdi-robot</v-icon>
        </v-avatar>
        <div style="width: 100%">
          <div class="d-flex align-center mb-2 justify-space-between">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-medium">{{ $t('components.AI.AiSummary.aiSummary') }}</span>
              <v-chip x-small class="ml-2" color="primary" label text-color="white">{{ $t('components.AI.AiSummary.noisy') }}</v-chip>
            </div>
          </div>
          <div class="text-body-2" style="line-height: 1.6">
            <div v-if="isLoading || isGenerating" class="d-flex align-center">
              <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
              {{ isLoading ? $t('components.AI.AiSummary.loadingSummaries') : $t('components.AI.AiSummary.generating') }}
            </div>
            <div v-else class="formatted-text">
              <div class="summaries-grid">
                <v-row>
                  <v-col
                    v-for="(summary, id) in summaries"
                    :key="id"
                    :cols="selectedSummary === id ? 12 : 4"
                    @click="selectedSummary = selectedSummary === id ? null : id"
                    class="transition-swing"
                  >
                    <v-card
                      :class="{
                        'summary-card': true,
                        'selected-card': selectedSummary === id,
                        'clickable': true,
                        'hover-effect': selectedSummary !== id
                      }"
                      :elevation="selectedSummary === id ? 4 : 1"
                      rounded="lg"
                    >
                      <v-card-title class="d-flex align-center py-3 px-4">
                        <v-icon
                          :color="selectedSummary === id ? 'primary' : 'grey darken-1'"
                          class="mr-2"
                          small
                        >
                          mdi-file-document-outline
                        </v-icon>
                        <span
                          class="text-subtitle-1 font-weight-medium"
                          :class="{'primary--text': selectedSummary === id}"
                        >
                          {{ summary.title }}
                        </span>
                        <v-spacer></v-spacer>
                        <v-icon
                          small
                          :color="selectedSummary === id ? 'primary' : 'grey'"
                        >
                          {{ selectedSummary === id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        </v-icon>
                      </v-card-title>
                      <v-divider v-if="!selectedSummary || selectedSummary === id"></v-divider>
                      <v-card-text
                        v-if="!selectedSummary || selectedSummary === id"
                        class="py-4 px-4"
                      >
                        <vue-markdown
                          :source="summary.text"
                          class="markdown-content"
                          :class="{
                            'truncated': selectedSummary !== id,
                            'expanded': selectedSummary === id
                          }"
                          :anchor-attributes="{class: 'disabled-link', disabled: true}"
                          :breaks="true"
                          :html="false"
                          :linkify="false"
                        ></vue-markdown>
                        <div class="d-flex justify-end align-center mt-2" @click.stop>
                          <v-btn
                            icon
                            small
                            class="mr-2"
                            :color="summary.vote === 'up' ? 'success' : ''"
                            @click="voteSummary(id, 'up')"
                          >
                            <v-icon small>mdi-thumb-up</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            small
                            :color="summary.vote === 'down' ? 'error' : ''"
                            @click="openDownvoteDialog(id)"
                          >
                            <v-icon small>mdi-thumb-down</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                   </v-card>
                </v-col>
              </v-row>

              <!-- Downvote Dialog -->
              <v-dialog v-model="downvoteDialog" max-width="500px">
                <v-card>
                  <v-card-title></v-card-title>
                  <v-card-text>
                    <v-textarea
                      v-model="downvoteComment"
                      :label="$t('components.AI.AiSummary.feedbackOptional')"
                      rows="3"
                      :placeholder="$t('components.AI.AiSummary.feedbackPlaceholder')"
                    ></v-textarea>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="submitDownvote"
                    >
                      {{ $t('components.AI.AiSummary.done') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </div>
        </div>
        <div class="d-flex">
          <v-btn
            x-small
            text
            color="primary"
            class="mt-4 px-0"
            v-if="Object.keys(summaries).length < 1 && !isLoading"
            @click="generateSummaries"
          >
            <v-icon small class="mr-1">mdi-robot</v-icon>
            {{ $t('components.AI.AiSummary.generate') }}
          </v-btn>
        </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment'
import numbro from 'numbro'
import { mapActions } from 'vuex'
import VueMarkdown from 'vue-markdown'

export default {
  props: {
    modelReport: {
      type: Object,
      required: true
    },
    clientId: {
      type: String,
      required: true
    },
    authUser: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isGenerating: false,
      isLoading: false,
      summaries: {},
      moment,
      selectedSummary: null,
      downvoteDialog: false,
      downvoteComment: '',
      downvoteTargetId: null
    }
  },
  computed: {
    generatedPrompts () {
      const startDate = this.modelReport.start_date
      const endDate = this.modelReport.end_date
      const totalSpend = Object.values(this.modelReport.invest).reduce((sum, value) => sum + value, 0)
      const totalSales = this.modelReport.total
      const modelDates = this.modelReport.v2.model_fit.Real.x
      const totalSalesTimeSeries = this.modelReport.v2.model_fit.Real.y
      const totalSalesTimeSeriesPredicted = this.modelReport.v2.model_fit.Prediction.y
      const totalSpendTimeSeries = this.sumYValues(this.modelReport.v2.media_spend)
      const mape = this.modelReport.v2.model_quality.MAPE
      const rae = this.modelReport.v2.model_quality.RAE
      const correlation = this.modelReport.v2.model_quality.Correlation
      const salesComponentsTotal = JSON.stringify(this.modelReport.v2.sales_decomposition_total)
      const salesComponentsTimeSeries = JSON.stringify(this.modelReport.v2.sales_decomposition_by_period)

      const generalSummary = '' +
        `Start Date: ${startDate}\n` +
        `End Date: ${endDate}\n` +
        `Total spend: ${totalSpend}\n` +
        `Total sales (not all is attributable to spend): ${totalSales}\n` +
        `Dates (for time-series reference): ${modelDates}` +
        `Sales time-series: ${totalSalesTimeSeries}\n` +
        `Spend time-series: ${totalSpendTimeSeries}\n`

      const modelQuality = '' +
        `Dates (for time-series reference): ${modelDates}` +
        `Real sales time-series: ${totalSalesTimeSeries}\n` +
        `Predicted sales time-series: ${totalSalesTimeSeriesPredicted}\n` +
        `MAPE: ${mape}` +
        `RAE: ${rae}` +
        `Correlation: ${correlation}`

      const componentSales = '' +
        `Total sales decomposition: ${salesComponentsTotal}` +
        `Sales decomposition time-series: ${salesComponentsTimeSeries}`

      const mediaSpendTimeSeries = '' +
        `Spend by media channel time-series: ${JSON.stringify(this.modelReport.v2.media_spend)}`

      const mediaAttributionTimeSeries = '' +
        `Attributed sales by media channel time-series: ${JSON.stringify(this.modelReport.v2.media_attributed_sales_decomposition_by_period)}`

      const mediaRoi = '' +
        `ROAS by media channel: ${JSON.stringify(this.modelReport.v2.quarterly_roi_average_media)}`

      const attributionDisparities = '' +
        `Spend by media channel time-series: ${JSON.stringify(this.modelReport.v2.media_spend)}` +
        `Attributed sales by media channel time-series: ${JSON.stringify(this.modelReport.v2.media_attributed_sales_decomposition_by_period)}`

      const mediaChannelCorrelationData = this.analyzeMediaCorrelations(this.modelReport.v2.media_spend, this.modelReport.v2.media_attributed_sales_decomposition_by_period)
      const modelHasSaturationRegions = this.modelReport.saturation[Object.keys(this.modelReport.saturation)[0]]['regions']
      let saturationOptimalZones
      let saturationFastAndSlow
      if (modelHasSaturationRegions) {
        saturationOptimalZones = this.saturationOptimalZonesPrompt(this.modelReport.saturation)
        saturationFastAndSlow = this.analyzeSaturationCurves(this.modelReport.saturation)
      }
      const sections = [
        {
          id: 'general_summary',
          title: this.$t('components.AI.AiSummary.generalSummary'),
          data: generalSummary,
          prompt: 'You have to analyze data from an MMM report. Generate a summary (1 paragraph, maximum of 4 lines) with the provided information. Also, analyze whether the marketing investment is consistent and how it varies over time, for example if there is a periodic peak in sales and spend and in which months (or dates).'
        },
        {
          id: 'model_quality',
          title: this.$t('components.AI.AiSummary.modelQuality'),
          data: modelQuality,
          prompt: 'You need to analyze data from an MMM report. Generate a summary (1 paragraph, maximum of 4 lines) using the provided information. Focus on analyzing the values obtained in the metrics and what each one implies, and also compare them with the shape of the series (actual and predicted).'
        },
        {
          id: 'base_sales',
          title: this.$t('components.AI.AiSummary.baseSales'),
          data: componentSales,
          prompt: 'You need to analyze data from an MMM report. Generate a summary (1 paragraph, maximum of 4 lines) using the provided information. Focus on analyzing the behavior of the Base when there are no other components. Indicate whether it is increasing, decreasing, remaining constant, or varying. Indicate if the trend suggest impact of underlying factors such as seasonality, economic conditions, and competitive pressures, independent of advertising efforts. Also, show the average baseline sales value. Add descriptive analysis to the previously mentioned requierements.'
        }
      ]
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('competition'))) {
        sections.push({
          id: 'competition',
          title: this.$t('components.AI.AiSummary.competition'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the competition marketing component to these sales for each date, and the attribution of various components to sales over time. Analyze the data and generate a detailed summary (1 paragraph, maximum of 6 lines) focusing on the impact of the competition marketing component. Only consider the impact of competition marketing when its attribution is significant, either because of important variations in its value or because it reaches a high level during the period. Highlight periods where competition\'s marketing impact shows a strong influence on sales and assess whether this is associated with an increase or decrease in sales. Identify periods where the competition\'s component becomes highly significant and evaluate if other marketing strategies or external factors helped to mitigate or neutralize its potential negative effects. Provide insights on what the trends indicate for future strategic adjustments.'
        })
      }
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('pricing'))) {
        sections.push({
          id: 'pricing',
          title: this.$t('components.AI.AiSummary.pricing'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the pricing component to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 6 lines) that focuses on the impact of pricing component on sales. Evaluate whether significant variations in pricing have affected sales and discuss the relative importance of pricing as a component, considering other components for context if needed. Note that if the pricing attribution is similar between two dates but sales still show significant variation, the prigcing component is less important. Similarly, if pricing varies but sales do not, this suggests limited impact. Ensure that only significant variations in pricing relative to total sales volume are highlighted.'
        })
      }
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('distribution'))) {
        sections.push({
          id: 'distribution',
          title: this.$t('components.AI.AiSummary.distribution'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the distribution component to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 6 lines) that focuses on the impact of the distribution component on sales. Evaluate whether significant variations in distribution have affected sales and discuss the relative importance of distribution as a component, considering other components for context if needed. Note that if the distribution attribution is similar between two dates but sales still show significant variation, the distribution component is less important. Similarly, if distribution varies but sales do not, this suggests limited impact. Ensure that only significant variations in distribution relative to total sales volume are highlighted.'
        })
      }
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('economic'))) {
        sections.push({
          id: 'economic',
          title: this.$t('components.AI.AiSummary.economic'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the economic component to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 6 lines) that focuses on the impact of economic component on sales. Evaluate whether significant variations in economic have affected sales and discuss the relative importance of economic, considering other components for context if needed. Analyze if there is a correlation between economic and KPI. Note that if the economic attribution is similar between two dates but sales still show significant variation, the economic component is less important. Similarly, if economic varies but sales do not, this suggests limited impact.'
        })
      }
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('instant marketing'))) {
        sections.push({
          id: 'instant_marketing_sales',
          title: this.$t('components.AI.AiSummary.instantMarketingSales'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of marketing components (Instant Marketing and Carry-over Marketing) to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 6 lines) that focuses on the impact of marketing on sales, where marketing corresponds to both Instant Marketing and Carry-over Marketing. Evaluate whether significant variations in marketing have affected sales and discuss its relative importance, considering other components for context if needed. Analyze if there is a correlation between marketing and KPI. Also, make a descriptive analysis of the marketing inversion it effectiveness and possible improvements.'
        })
      }
      /* if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('carry-over marketing'))) {
        sections.push({
          id: 'cary_over_marketing_sales',
          title: 'Carry-over Marketing Sales',
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the seasonality component to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 4 lines) that focuses on the impact of seasonality component on sales. Evaluate whether significant variations in seasonality have affected sales and discuss the its relative importance, considering other components for context if needed. Analyze if there is a correlation between seasonality and KPI. Note that if the seasonality attribution is similar between two dates but sales still show significant variation, the component is less important. Similarly, if seasonality varies but sales do not, this suggests limited impact. In contrast, if seasonality varies and also do KPI, then there is a correlation.'
        })
      } */
      if (this.modelReport.v2.sales_decomposition_total.components.find(item => item.name.toLowerCase().includes('seasonality'))) {
        sections.push({
          id: 'seasonality',
          title: this.$t('components.AI.AiSummary.seasonality'),
          data: componentSales,
          prompt: 'You have access to data from an MMM report, which includes total sales per period, the contribution of the seasonality component to these sales for each date, and the attribution of various components to sales over time. Analyze this data and generate a concise summary (up to 4 lines) that focuses on the impact of seasonality component on sales. Evaluate whether significant variations in seasonality have affected sales and discuss the its relative importance, considering other components for context if needed. Analyze if there is a correlation between seasonality and KPI. Note that if the seasonality attribution is similar between two dates but sales still show significant variation, the component is less important. Similarly, if seasonality varies but sales do not, this suggests limited impact. In contrast, if seasonality varies and also do KPI, then there is a correlation.'
        })
      }
      // TODO: Outliers
      sections.push({
        id: 'component_correlations',
        title: this.$t('components.AI.AiSummary.componentCorrelations'),
        data: componentSales,
        prompt: 'You have access to data from an MMM report that includes pairs of components with the highest correlations across the entire time span, the dates corresponding to the sales decomposition series, and the series of sales decomposition for each component. Create a concise analysis (up to two paragraphs) that evaluates all components with significant correlations to others. Use the list of highly correlated components to examine which ones consistently show strong correlations over the entire time period. Provide an interpretation of the underlying causes and implications of these correlations, along with strategic suggestions based on this analysis. Additionally, analyze the sales decomposition data to identify components that are highly correlated over a specific period of at least six months. Justify your findings by referencing the corresponding values in the sales decomposition series and explain why these components are correlated. Ensure not inventing values or making any errors in data representation. If there are no pairs meeting these criteria, simply state that. Provide a detailed description of their correlation, potential causes, implications, and future-oriented suggestions. Important: When multiple correlated component pairs are present, include all of them in your analysis. Avoid recommending further investigations, analyses, or solutions for past periods. Only propose suggestions applicable to future strategy.'
      })
      sections.push({
        id: 'top_media_by_spend',
        title: this.$t('components.AI.AiSummary.topMediaBySpend'),
        data: mediaSpendTimeSeries,
        prompt: 'You have access to data from an MMM report, which includes the media spend per period, and the dates of each point. Analyze this data and generate a concise summary (up to 6 lines) that focuses on describe investment priorities and strategies throughout the analyzed period. Highlight the media with the highest investment. Also, perform the analysis separated by digital, non-digital, and traditional media.'
      })
      sections.push({
        id: 'top_media_by_attribution',
        title: this.$t('components.AI.AiSummary.topMediaByAttribution'),
        data: mediaAttributionTimeSeries,
        prompt: 'You have access to data from an MMM report, which includes the media attributed sales per period, and the dates of each point. Analyze this data and generate a concise summary (up to 6 lines) that focuses on mention the media that has more attribution in sales and how it varies over time. Also, perform the analysis separated by digital and non-digital media.'
      })
      sections.push({
        id: 'attribution_disparities',
        title: this.$t('components.AI.AiSummary.attributionDisparities'),
        data: attributionDisparities,
        prompt: 'You have access to data from an MMM report, which includes the media spend, the media attributed sales. Analyze this data and generate a concise summary (up to 6 lines) that focuses on mention the media that has a big disparity between investment and attributed sales. Also, perform the analysis separated by digital and non-digital media.'
      })
      sections.push({
        id: 'fast_saturating_media',
        title: this.$t('components.AI.AiSummary.fastSaturatingMedia'),
        data: saturationFastAndSlow,
        prompt: 'You have access to data from an MMM report, which includes the media with slowest and fastest saturation, and also both saturation curves. Analyze this data and generate a concise summary (up to 6 lines) that focuses on mention its possible causes and implications. Saturation curves have both x and y axes, where the x-axis represents the investment amount and the y-axis represents the sales amount. It is important to consider both axes when analyzing the saturation curve, and especially to accurately determine the point on the x-axis where investment reaches saturation, ensuring that it correlates with the point on the y-axis where the curve starts to show a slow growth. Important: Argue your results using the corresponding values. Be careful not to invent values or confuse media'
      })
      if (modelHasSaturationRegions) {
        sections.push({
          id: 'saturation_analysis',
          title: this.$t('components.AI.AiSummary.saturationAnalysis'),
          data: saturationOptimalZones,
          prompt: 'You have access to data from an MMM report, which includes information about the optimality of the investments for each media. Analyze this data and generate a concise summary (up to 6 lines) that focuses on mention which media requires adjudments in its investments strategies. Suggest improving the investment only for those media that have approximately 20% of points in the suboptimal or overoptimal zone, or both. Important: Argue your results using the corresponding values. Be careful not to invent values or confuse media. Don\'t mention the 20% threshold'
        })
      }
      sections.push({
        id: 'high_and_low_roi',
        title: this.$t('components.AI.AiSummary.highAndLowRoi'),
        data: mediaRoi,
        prompt: 'You have access to data from an MMM report that provides insights into the ROI over time for each media channel. Analyze this data to create a concise summary (up to 6 lines in one paragraph) that describes the variations in ROI and highlights key findings. Include recommendations for future strategies. Important: Argue your results using the corresponding values. Be careful not to invent values or confuse media. Don\'t propose further investigation or evaluation.'
      })
      sections.push({
        id: 'media_channel_strategies',
        title: this.$t('components.AI.AiSummary.mediaChannelStrategies'),
        data: mediaSpendTimeSeries,
        prompt: 'You have access to data from an MMM report, which includes the media spend per period, and the dates of each point. Analyze this data and generate a concise summary (up to 6 lines) that focuses on classifying media investments in the following categories: Always on, Flighting, Pulsing, or Low Investment. Organize the summary by category, not by media. Consider the following definitions for each category. Always-on refers to maintaining continuous spending throughout the entire time span, without any periods of zero investment. Flighting refer to a strategy of heavy investment interspersed with periods of lower spend. Pulsing is a consistent baseline level of investment punctuated by increases in key moments. Low Investment when the media have consistently low investment level. Important: Argue your results. Be careful not to invent values or confuse media. Don\'t propose further investigation or evaluation.'
      })
      if (mediaChannelCorrelationData && Object.keys(mediaChannelCorrelationData).length > 0) {
        sections.push({
          id: 'media_channel_correlations',
          title: this.$t('components.AI.AiSummary.mediaChannelCorrelations'),
          data: mediaChannelCorrelationData,
          prompt: 'You have access to data from an MMM report, which includes the pairs where the spending of one medium is highly correlated with the attributed sales of another. Analyze this data and generate a concise summary (up to 6 lines) that focuses on describing posible causes and implications of these correlations. Important: Argue your results using the corresponding values. Be careful not to invent values or confuse media. Don\'t propose further investigation or evaluation'
        })
      } else {
        console.log('No correlations found')
      }
      return sections
    }
  },
  methods: {
    ...mapActions('ai', [
      'generateModelReportAiSummaries',
      'saveAiSummaries',
      'fetchModelReportSummaries',
      'updateAiSummaryVote'
    ]),
    async submitDownvote () {
      this.downvoteDialog = false
      try {
        await this.voteSummary(this.downvoteTargetId, 'down', this.downvoteComment.trim())
        this.downvoteComment = ''
      } catch (error) {
        console.error('Error submitting downvote:', error)
        this.$emit('error', error)
      }
    },
    openDownvoteDialog (summaryId) {
      this.downvoteTargetId = summaryId
      this.downvoteComment = this.summaries[summaryId].voteComment || ''
      this.downvoteDialog = true
    },
    async voteSummary (summaryId, voteType, voteComment) {
      try {
        // Get the current summary
        const summary = this.summaries[summaryId]
        // Update local state
        this.summaries = {
          ...this.summaries,
          [summaryId]: {
            ...summary,
            vote: voteType,
            voteComment: voteComment
          }
        }
        // Save to Firebase
        await this.updateAiSummaryVote({
          clientId: this.clientId,
          userId: this.authUser.uid,
          summaryId: summaryId,
          vote: voteType,
          comment: voteComment
        })
      } catch (error) {
        console.error('Error updating vote:', error)
        this.$emit('error', error)
        // Revert local state in case of error
        this.loadSavedSummaries()
      }
    },
    analyzeSaturationCurves (input) {
      // Helper function to calculate growth rate at each point
      function calculateGrowthRate (curve) {
        const growthRates = []
        for (let i = 1; i < curve.x.length; i++) {
          const deltaX = curve.x[i] - curve.x[i - 1]
          const deltaY = curve.y[i] - curve.y[i - 1]
          const rate = deltaY / deltaX
          growthRates.push(rate)
        }
        return growthRates.reduce((a, b) => a + b) / growthRates.length
      }
      // Find slowest and fastest curves
      let slowestRate = Infinity
      let fastestRate = -Infinity
      let slowestCurve = null
      let fastestCurve = null
      for (const [channel, data] of Object.entries(input)) {
        const growthRate = calculateGrowthRate(data)
        if (growthRate < slowestRate) {
          slowestRate = growthRate
          slowestCurve = {
            x: data.x,
            y: data.y,
            regions: data.regions,
            mediaChannelName: channel
          }
        }
        if (growthRate > fastestRate) {
          fastestRate = growthRate
          fastestCurve = {
            x: data.x,
            y: data.y,
            regions: data.regions,
            mediaChannelName: channel
          }
        }
      }
      return {
        'Slowest Saturation': slowestCurve,
        'Fastest Saturation': fastestCurve
      }
    },
    saturationOptimalZonesPrompt (data) {
      let output = ''
      for (let media of Object.keys(data)) {
        output += '\n' + media + '\n'
        // Extract datapoints and regions
        const datapoints = data[media]['datapoints']
        const regions = data[media]['regions']
        // Counter for intervals
        const intervalCounts = new Array(regions.length + 1).fill(0)
        // Count how many datapoints fall into each interval from regions list
        for (let datapoint of datapoints) {
          const xValue = datapoint.x
          if (xValue >= regions[regions.length - 1]) {
            intervalCounts[intervalCounts.length - 1]++
          } else {
            for (let i = 0; i < regions.length; i++) {
              if (xValue <= regions[i]) {
                intervalCounts[i]++
                break
              }
            }
          }
        }
        const finalIntervalCounts = [
          intervalCounts[0] + intervalCounts[1],
          intervalCounts[2] + intervalCounts[3],
          intervalCounts[4]
        ]
        const total = finalIntervalCounts.reduce((a, b) => a + b, 0)
        // Add results to output string
        output += `Points below optimal: ${(finalIntervalCounts[0] * 100 / total).toFixed(1)}%\n`
        output += `Points above optimal: ${(finalIntervalCounts[2] * 100 / total).toFixed(1)}%\n`
        output += `Points in optimal zone: ${(finalIntervalCounts[1] * 100 / total).toFixed(1)}%\n`
      }
      return output
    },
    calculateCorrelation (x, y) {
      const n = x.length
      // Calculate means
      const xMean = x.reduce((a, b) => a + b, 0) / n
      const yMean = y.reduce((a, b) => a + b, 0) / n
      // Calculate covariance and standard deviations
      let covariance = 0
      let xStdDev = 0
      let yStdDev = 0
      for (let i = 0; i < n; i++) {
        const xDiff = x[i] - xMean
        const yDiff = y[i] - yMean
        covariance += xDiff * yDiff
        xStdDev += xDiff * xDiff
        yStdDev += yDiff * yDiff
      }
      xStdDev = Math.sqrt(xStdDev / n)
      yStdDev = Math.sqrt(yStdDev / n)
      // Calculate correlation
      return covariance / (n * xStdDev * yStdDev)
    },
    analyzeMediaCorrelations (mediaSpend, mediaAttribution) {
      const correlations = {}
      // Get all media channels
      const spendChannels = Object.keys(mediaSpend)
      const attributionChannels = Object.keys(mediaAttribution)
      // Calculate correlations for each pair
      spendChannels.forEach(spendChannel => {
        const spendData = mediaSpend[spendChannel].y
        attributionChannels.forEach(attrChannel => {
          const attrData = mediaAttribution[attrChannel].y
          // Calculate correlation coefficient
          const correlation = this.calculateCorrelation(spendData, attrData)
          const pairName = `${spendChannel} Spend - ${attrChannel} Attribution`
          correlations[pairName] = correlation
        })
      })
      // Filter correlations above threshold and build output string
      const threshold = 0.75
      return Object.entries(correlations)
        .filter(([_, value]) => Math.abs(value) > threshold)
        .map(([pair, value]) => `${pair}: ${value.toFixed(2)}`)
        .join(' ')
    },
    sumYValues (data) {
      // Get all property keys (p1, p2, etc.)
      const properties = Object.keys(data)
      // Get the length of y array (assuming all y arrays are the same length)
      const arrayLength = data[properties[0]].y.length
      // Initialize result array with zeros
      const result = new Array(arrayLength).fill(0)
      // Sum y values for each position
      properties.forEach(prop => {
        data[prop].y.forEach((value, index) => {
          result[index] += value
        })
      })
      return result
    },
    async loadSavedSummaries () {
      this.isLoading = true
      try {
        const summaries = await this.fetchModelReportSummaries({
          clientId: this.clientId,
          userId: this.authUser.uid
        })
        this.summaries = summaries
      } catch (error) {
        console.error('Error loading saved summaries:', error)
        this.$emit('error', error)
      } finally {
        this.isLoading = false
      }
    },
    async saveSummariesToFirebase (summaries) {
      try {
        await this.saveAiSummaries({
          clientId: this.clientId,
          userId: this.authUser.uid,
          summaries: summaries
        })
      } catch (error) {
        console.error('Error saving AI summary version:', error)
        this.$emit('error', error)
      }
    },
    async generateSummaries () {
      this.isGenerating = true
      const globalPrompt = 'Important: Give your answers using markdown. Never use titles or subtitles. Always make proper nouns bold, also always make acronyms bold. Dont use bulleted lists or such, just paragraphs. If you give big numbers use k, m aproximation notation (eg. 12.3m). Dont talk about "The MMM report" in any way, just give the comments. Dont ever give full dates, just mention years (eg. 2023) or months (eg. May 2024)'
      let summaries = {}
      try {
        // Process summaries in smaller batches to avoid timeout
        const batchSize = 3
        const sections = this.generatedPrompts

        for (let i = 0; i < sections.length; i += batchSize) {
          const batch = sections.slice(i, i + batchSize)

          const batchPromises = batch.map(section =>
            this.generateModelReportAiSummaries({
              modelData: section.data,
              systemPrompt: `${section.prompt}. ${globalPrompt}`
            }).then(sectionText => ({
              id: section.id,
              title: section.title,
              text: sectionText
            })).catch(error => ({
              id: section.id,
              title: section.title,
              text: this.$t('components.AI.AiSummary.errorGeneratingSection'),
              error: error
            }))
          )

          try {
            const batchResults = await Promise.all(batchPromises)

            // Add batch results to summaries
            batchResults.forEach(result => {
              summaries[result.id] = {
                title: result.title,
                text: result.text
              }
            })

            // Update UI with partial results
            this.summaries = { ...summaries }
          } catch (error) {
            console.error('Error generating batch summaries:', error)
          }

          // Small delay between batches
          if (i + batchSize < sections.length) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }

        // Save summaries to Firebase
        await this.saveSummariesToFirebase(summaries)
      } catch (error) {
        console.error('Error generating AI summaries:', error)
        this.$emit('error', error)
      } finally {
        this.isGenerating = false
      }
    },
    formatDate (date) {
      return moment(date).format('MMM YYYY')
    },
    formatValue (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    }
  },
  components: {
    VueMarkdown
  },
  async created () {
    await this.loadSavedSummaries()
  }
}
</script>

<style scoped>
.summary-card {
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.hover-effect:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.selected-card {
  border: 2px solid var(--v-primary-base);
}

.clickable {
  cursor: pointer;
}

.markdown-content {
  line-height: 1.6;
  color: #424242;
}

.truncated {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 100px;
}

.expanded {
  max-height: none;
}

.v-card-title {
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.selected-card .v-card-title {
  background-color: #f5f5f5;
}

.formatted-text {
  white-space: pre-line;
}

.markdown-content :deep(p) {
  margin-bottom: 1em;
}

.markdown-content :deep(strong) {
  font-weight: 600;
}

.markdown-content :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5em;
  margin-bottom: 1em;
}

.markdown-content :deep(li) {
  margin-bottom: 0.5em;
}
.custom-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.navigation-btn {
  z-index: 1;
}

.version-select {
  margin: 0 -36px !important;
  padding: 0 36px;
  min-width: 200px;
}
</style>
